import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import AccessDenied from './AccessDenied';
import { SUPER_ADMIN_EMAIL } from '../config';
import { useNavigate } from 'react-router-dom';
import { createNavigateWrapper } from '../utils/navigation';

/**
 * Composant pour protéger les routes avec authentification et permissions
 */
const ProtectedRoute = ({ 
  children, 
  requiredRole = null, 
  requiredRoles = [], 
  requiredFeature = null,
  enabledFeatures = [],
  currentPlan = null,
  deniedSection = null
}) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const navigateWrapper = createNavigateWrapper(navigate);

  // Si pas d'utilisateur (token manquant ou expiré), rediriger vers website (landing)
  if (!user) {
    const next = encodeURIComponent(`${location.pathname || '/'}${location.search || ''}`);
    return <Navigate to={`/?auth=true&needLogin=1&next=${next}`} replace />;
  }

  // Vérifier le rôle requis
  if (requiredRole || requiredRoles.length > 0) {
    const rolesToCheck = requiredRole ? [requiredRole] : requiredRoles;
    const isAdminOrManager = rolesToCheck.includes('admin_or_manager');
    
    if (isAdminOrManager) {
      if (!['admin', 'manager'].includes(user.role)) {
        // Employee trying to access admin/manager route
        if (user.role === 'employee') {
          if (deniedSection === 'billing') {
            const isSuperAdmin = (user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL || user?.role === 'super_admin';
            return <AccessDenied onNavigate={navigateWrapper} requiredRole="admin_or_manager" deniedSection="billing" isSuperAdmin={isSuperAdmin} />;
          }
          return <Navigate to="/home" replace />;
        }
        return <AccessDenied onNavigate={navigateWrapper} requiredRole="admin_or_manager" deniedSection={deniedSection} isSuperAdmin={(user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL || user?.role === 'super_admin'} />;
      }
    } else if (!rolesToCheck.includes(user.role)) {
      // Employee trying to access admin/manager-only route
      if (user.role === 'employee' && rolesToCheck.some(r => ['admin', 'manager'].includes(r))) {
        // Billing/plans: show Access Denied with clear message instead of redirecting
        if (deniedSection === 'billing') {
          const isSuperAdmin = (user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL || user?.role === 'super_admin';
          return <AccessDenied onNavigate={navigateWrapper} requiredRole="admin_or_manager" deniedSection="billing" isSuperAdmin={isSuperAdmin} />;
        }
        return <Navigate to="/home" replace />;
      }
      return <AccessDenied onNavigate={navigateWrapper} requiredRole={requiredRole || requiredRoles[0]} deniedSection={deniedSection} isSuperAdmin={(user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL || user?.role === 'super_admin'} />;
    }
  }

  // Vérifier la feature requise
  if (requiredFeature) {
    // Si pas de features chargées, autoriser l'accès (pour éviter de bloquer avant le chargement)
    if (enabledFeatures.length > 0 && !enabledFeatures.includes(requiredFeature)) {
      return <AccessDenied onNavigate={navigateWrapper} missingFeature={requiredFeature} currentPlan={currentPlan} />;
    }
  }

  return children;
};

export default ProtectedRoute;

